<template>
  <LinkWrapper
    v-bind="$attrs"
    :href="event?.eventUrl"
    target="_blank"
    @click="() => handleClick()"
  >
    <TeamBanner
      :eventId="eventId"
      :slope="layout === 'row' ? 'top' : 'bottom'"
      :badge="competitorData[0].badge"
      :countryCode="competitorData[0].colors?.country_code_a2"
      :customSvgPath="customSvgPaths.left"
      :disableBadge="disableBadge"
      :name="displayName ? competitorData[0].displayName : undefined"
      :height="displayName ? (totalBannerHeight < 30 ? 24 : 34) : 46"
      :customSvgAspectRatio="customSvgAspectRatio"
      homeOrAwayTeam="home"
      alignment="left"
      @bannerLoaded="leftBanner"
    />
  </LinkWrapper>

  <LinkWrapper
    v-if="showVs"
    v-bind="$attrs"
    :href="event?.eventUrl"
    target="_blank"
    class="vs"
    style="display: flex; minHeight: '0'; height: auto;"
    @click="() => handleClick()"
  >
    <TeamVersusIcon
      :eventId="eventId"
      :class="classes.vs"
      v-bind="$attrs"
      :horizontalLine="vsHorizontal"
    />
  </LinkWrapper>

  <LinkWrapper
    v-bind="$attrs"
    :href="event?.eventUrl"
    target="_blank"
    @click="() => handleClick()"
  >
    <TeamBanner
      :eventId="eventId"
      :alignment="layout === 'row' ? 'right' : 'left'"
      :slope="'top'"
      :customSvgPath="customSvgPaths?.right"
      :badge="competitorData[1].badge"
      :countryCode="competitorData[1].colors?.country_code_a2"
      :name="displayName ? competitorData[1].displayName : undefined"
      :height="displayName ? (totalBannerHeight < 30 ? 24 : 34) : 46"
      :customSvgAspectRatio="customSvgAspectRatio"
      :disableBadge="disableBadge"
      homeOrAwayTeam="away"
      @bannerLoaded="rightBanner"
    />
  </LinkWrapper>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  PropType,
  reactive,
  ref,
  onMounted,
  ComputedRef
} from "vue";
import TeamBanner from "./TeamBanner.vue";
import TeamVersusIcon from "./TeamVersusIcon.vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import {
  composeEvent,
  composeEventCompetitors
} from "@fresh8/ad-data-composition/src/betData";
import LinkWrapper from "./LinkWrapper.vue";
import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import { SportCompetitor } from "@fresh8/types/src/betData";
import { injectTeamColors } from "../lib/teamBranding";

const useStyle = createUseStyles({
  vs: {
    justifySelf: "center",
    alignSelf: "center",
    height: props => props.height + "px",
    minHeight: "0"
  }
});

export default defineComponent({
  name: "Competitors",
  components: {
    TeamBanner,
    TeamVersusIcon,
    LinkWrapper
  },
  props: {
    eventId: {
      type: String,
      required: true
    },
    displayName: {
      type: Boolean,
      default: true
    },
    forceTeamName: {
      type: String as PropType<"full" | "short" | "micro">,
      default: undefined
    },
    layout: {
      type: String as PropType<"column" | "row">,
      required: true
    },
    customSvgPaths: {
      type: Object as PropType<Partial<Record<"left" | "right", string>>>,
      required: false,
      default: {
        left: "",
        right: ""
      } as Record<"left" | "right", string>
    },
    customSvgAspectRatio: {
      type: String as PropType<string>,
      require: false,
      default: ""
    },
    vsHorizontal: {
      type: Boolean,
      required: false,
      default: false
    },
    showVs: {
      type: Boolean,
      required: false,
      default: true
    },
    cardNumber: { type: String, required: false, default: "1" }
  },
  setup(props) {
    const competitors = composeEventCompetitors(props.eventId) as ComputedRef<
      SportCompetitor[]
    >;
    const event = composeEvent(props.eventId);
    const cardNumber = injectCardNumber();
    const teamColorsContext = injectTeamColors();

    const { handleClick } = useAnalytics(
      { eventId: props.eventId, cardNumber: cardNumber },
      "Event Name"
    );
    const bannerHeights = reactive({ left: 0, right: 0 });
    const totalBannerHeight = ref(0);
    const isMicro = computed(() =>
      [bannerHeights.left, bannerHeights.right].some((i: number) => i > 30)
    );

    const disableBadge = computed(
      () => !teamColorsContext.teamIconsAvailable.value
    );

    const competitorData = computed(() => {
      return competitors.value.map(comp => ({
        ...comp,
        displayName: props.forceTeamName
          ? comp.name[props.forceTeamName]
          : isMicro.value
          ? comp.name.micro
          : comp.name.short
      }));
    });

    onMounted(() => {
      if (isMicro.value) {
        totalBannerHeight.value = 24;
      } else {
        totalBannerHeight.value = Math.max(
          bannerHeights.left,
          bannerHeights.right
        );
      }
    });

    const classes = useStyle(
      computed(() => ({
        height: totalBannerHeight.value
      }))
    );

    return {
      disableBadge,
      leftBanner: data => (bannerHeights.left = data?.height),
      rightBanner: data => (bannerHeights.right = data?.height),
      bannerHeights,
      event,
      handleClick,
      classes,
      competitorData,
      totalBannerHeight
    };
  }
});
</script>

<style></style>
